import React, { useState, useEffect, useRef } from 'react';
import { X, Eye, Code, Monitor, Tablet, Smartphone, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { buildTemplatePreviewHtml } from '../utils/buildTemplatePreviewHtml';

const TemplatePreviewModal = ({ template, onClose, onSelect }) => {
  const [viewMode, setViewMode] = useState('preview');
  const [deviceMode, setDeviceMode] = useState('desktop');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, []);

  if (!template) return null;

  const handleClose = () => {
    document.body.style.overflow = '';
    onClose();
  };

  const handleSelectTemplate = () => {
    document.body.style.overflow = '';
    onSelect(template);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target.id === 'modal-backdrop') handleClose();
  };

  const getFullHTML = () => buildTemplatePreviewHtml(template);

  // Device widths
  const deviceWidths = {
    desktop: '100%',
    tablet: '768px',
    mobile: '375px',
  };

  const DeviceButton = ({ mode, icon: Icon, label }) => (
    <button
      onClick={() => setDeviceMode(mode)}
      title={label}
      style={{
        padding: '6px 8px',
        borderRadius: '6px',
        border: 'none',
        cursor: 'pointer',
        background: deviceMode === mode ? 'rgba(139,92,246,0.2)' : 'transparent',
        color: deviceMode === mode ? '#a78bfa' : '#6b7280',
        transition: 'all 0.15s',
      }}
    >
      <Icon style={{ width: '15px', height: '15px' }} />
    </button>
  );

  return (
    <div
      id="modal-backdrop"
      className="fixed inset-0 flex items-center justify-center bg-black/80 p-3"
      style={{ zIndex: 99999 }}
      onClick={handleBackdropClick}
      data-testid="template-preview-modal"
    >
      <div
        className="bg-zinc-900 rounded-2xl w-full flex flex-col border border-white/10"
        style={{ maxWidth: 'calc(100vw - 24px)', height: 'calc(100vh - 24px)', maxHeight: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
          <div className="flex-1 min-w-0 mr-4 flex items-center gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold truncate" data-testid="modal-template-name">
                  {template.name}
                </h2>
                <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded-full text-xs font-medium capitalize flex-shrink-0">
                  {template.category?.replace(/_/g, ' ')}
                </span>
              </div>
              <p className="text-zinc-500 text-xs truncate">{template.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Device switcher (only in preview mode) */}
            {viewMode === 'preview' && (
              <div className="flex items-center gap-0.5 bg-zinc-800 rounded-lg p-0.5 mr-1">
                <DeviceButton mode="desktop" icon={Monitor} label="Desktop" />
                <DeviceButton mode="tablet" icon={Tablet} label="Tablet" />
                <DeviceButton mode="mobile" icon={Smartphone} label="Mobile" />
              </div>
            )}

            <Button
              onClick={() => {
                setViewMode(viewMode === 'preview' ? 'code' : 'preview');
                setIframeLoaded(false);
              }}
              variant="outline"
              className="border-zinc-700 text-xs px-3 py-1.5 h-auto"
              data-testid="toggle-view-mode"
            >
              {viewMode === 'preview'
                ? <><Code className="w-3.5 h-3.5 mr-1" />Code</>
                : <><Eye className="w-3.5 h-3.5 mr-1" />Preview</>
              }
            </Button>

            <Button
              onClick={handleClose}
              variant="ghost"
              className="text-zinc-400 hover:text-white px-2"
              data-testid="close-modal"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-hidden" style={{ position: 'relative', minHeight: 0 }}>
          {viewMode === 'preview' ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                background: '#f1f5f9',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                padding: deviceMode !== 'desktop' ? '16px 16px 0' : '0',
                overflowY: 'auto',
                minHeight: '500px',
              }}
            >
              {/* Loading overlay */}
              {!iframeLoaded && (
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', background: '#18181b', zIndex: 10,
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <Loader2 style={{ width: '28px', height: '28px', color: '#8b5cf6', animation: 'spin 1s linear infinite' }} />
                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>Loading preview...</span>
                  </div>
                </div>
              )}

              <div
                style={{
                  width: deviceWidths[deviceMode],
                  maxWidth: '100%',
                  height: deviceMode === 'desktop' ? '100%' : 'auto',
                  minHeight: '500px',
                  background: 'white',
                  boxShadow: deviceMode !== 'desktop' ? '0 8px 40px rgba(0,0,0,0.3)' : 'none',
                  borderRadius: deviceMode !== 'desktop' ? '12px 12px 0 0' : '0',
                  overflow: 'hidden',
                  transition: 'width 0.3s ease',
                }}
              >
                <iframe
                  ref={iframeRef}
                  srcDoc={getFullHTML()}
                  className="w-full border-0"
                  style={{
              height: '100%',
                  minHeight: '400px',
                    display: 'block',
                  }}
                  title="Template Preview"
                  data-testid="preview-iframe"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  onLoad={() => setIframeLoaded(true)}
                />
              </div>
            </div>
          ) : (
            /* ── Code view ── */
            <div className="h-full overflow-y-auto p-5 space-y-5" style={{ maxHeight: '65vh' }}>
              {/* Sections summary */}
              {(() => {
                const html = template.html_code || '';
                const sectionMatches = html.match(/data-section-name="([^"]+)"/g) || [];
                const sectionNames = sectionMatches.map(m => m.replace(/data-section-name="|"/g, ''));
                return sectionNames.length > 0 ? (
                  <div>
                    <h3 className="text-xs font-bold mb-2 text-zinc-400 uppercase tracking-wider">Sections ({sectionNames.length})</h3>
                    <div className="flex flex-wrap gap-2">
                      {sectionNames.map((name, i) => (
                        <span key={i} className="px-2.5 py-1 bg-violet-500/10 border border-violet-500/20 text-violet-400 rounded-full text-xs">
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              <div>
                <h3 className="text-xs font-bold mb-2 text-zinc-400 uppercase tracking-wider">HTML</h3>
                <pre className="bg-zinc-950 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed">
                  <code className="text-green-400">{template.html_code}</code>
                </pre>
              </div>
              <div>
                <h3 className="text-xs font-bold mb-2 text-zinc-400 uppercase tracking-wider">CSS</h3>
                <pre className="bg-zinc-950 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed">
                  <code className="text-blue-400">{template.css_code}</code>
                </pre>
              </div>
              {template.js_code && (
                <div>
                  <h3 className="text-xs font-bold mb-2 text-zinc-400 uppercase tracking-wider">JavaScript</h3>
                  <pre className="bg-zinc-950 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed">
                    <code className="text-yellow-400">{template.js_code}</code>
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/10 flex-shrink-0">
          {/* Section count info */}
          <div className="text-xs text-zinc-500">
            {(() => {
              const html = template.html_code || '';
              const count = (html.match(/data-section-name=/g) || []).length;
              return count > 0 ? `${count} section${count !== 1 ? 's' : ''}` : '';
            })()}
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleClose}
              variant="outline"
              className="border-zinc-700"
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSelectTemplate}
              className="bg-violet-500 hover:bg-violet-600 text-white font-semibold"
              data-testid="select-template-button"
            >
              Use This Template
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default TemplatePreviewModal;
